import React, { useEffect, useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { getReligionDistribution } from "../../services/adminService";
import { ChartPeriod } from "./ChartFilterBar";

interface ReligionItem {
  religion: string;
  count: number;
  percentage: number;
}

interface ReligionData {
  scholars: ReligionItem[];
  users: ReligionItem[];
  combined: ReligionItem[];
  bookings: ReligionItem[];
  summary: {
    totalScholars: number;
    totalUsers: number;
    totalCombined: number;
    totalBookings: number;
  };
}

const RELIGION_COLORS: Record<string, string> = {
  Hinduism: "#F97316", // Saffron / Orange
  Islam: "#10B981",    // Emerald Green
  Christianity: "#3B82F6", // Royal Blue
  Sikhism: "#F59E0B",  // Amber / Golden
  Buddhism: "#8B5CF6", // Purple / Violet
  Jainism: "#06B6D4",  // Cyan / Teal
  Judaism: "#EC4899",  // Pink / Rose
  Other: "#64748B",    // Slate
};

const FALLBACK_PALETTE = [
  "#6366F1", "#14B8A6", "#F43F5E", "#A855F7", "#84CC16", "#0EA5E9", "#D946EF"
];

const getColor = (religion: string, index: number) => {
  if (RELIGION_COLORS[religion]) return RELIGION_COLORS[religion];
  // Check case-insensitive
  const match = Object.keys(RELIGION_COLORS).find(
    k => k.toLowerCase() === religion.toLowerCase()
  );
  if (match) return RELIGION_COLORS[match];
  return FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
};

type AudienceCategory = "combined" | "scholars" | "users" | "bookings";

interface PieChartBoxProps {
  period?: ChartPeriod;
}

const PieChartBox: React.FC<PieChartBoxProps> = ({ period = "all" }) => {
  const [data, setData] = useState<ReligionData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [category, setCategory] = useState<AudienceCategory>("combined");

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getReligionDistribution(period);
        if (isMounted && res) {
          setData(res);
        }
      } catch (err) {
        console.error("Failed to load religion distribution:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [period]);

  const currentList = useMemo<ReligionItem[]>(() => {
    if (!data) return [];
    const list = data[category] || [];
    return list.filter(item => item.count > 0);
  }, [data, category]);

  const totalCount = useMemo(() => {
    if (!data?.summary) return currentList.reduce((acc, curr) => acc + curr.count, 0);
    switch (category) {
      case "scholars": return data.summary.totalScholars;
      case "users": return data.summary.totalUsers;
      case "bookings": return data.summary.totalBookings;
      case "combined":
      default:
        return data.summary.totalCombined;
    }
  }, [data, category, currentList]);

  const categories: { key: AudienceCategory; label: string; count?: number }[] = [
    { key: "combined", label: "All Platform", count: data?.summary?.totalCombined },
    { key: "scholars", label: "Scholars", count: data?.summary?.totalScholars },
    { key: "users", label: "Customers", count: data?.summary?.totalUsers },
    { key: "bookings", label: "Bookings", count: data?.summary?.totalBookings },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      {/* Audience selector tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 14,
          padding: "3px 4px",
          borderRadius: 10,
          background: "hsl(var(--muted) / 0.5)",
          width: "fit-content",
        }}
      >
        {categories.map((c) => {
          const isActive = category === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              style={{
                border: "none",
                background: isActive ? "hsl(var(--card))" : "transparent",
                color: isActive ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                padding: "4px 10px",
                borderRadius: 7,
                fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                cursor: "pointer",
                boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span>{c.label}</span>
              {c.count !== undefined && (
                <span
                  style={{
                    fontSize: 10,
                    padding: "1px 5px",
                    borderRadius: 99,
                    background: isActive ? "hsl(var(--primary) / 0.15)" : "hsl(var(--muted))",
                    color: isActive ? "hsl(var(--primary))" : "inherit",
                    fontWeight: 700,
                  }}
                >
                  {c.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chart container */}
      <div style={{ flex: 1, minHeight: 210, position: "relative" }}>
        {loading ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "hsl(var(--muted-foreground))",
              fontSize: 13,
            }}
          >
            Loading distribution...
          </div>
        ) : currentList.length === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "hsl(var(--muted-foreground))",
              fontSize: 13,
              gap: 4,
            }}
          >
            <span style={{ fontWeight: 600 }}>No religion data available</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>No records found for the selected filter</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={currentList}
                cx="50%"
                cy="48%"
                innerRadius={54}
                outerRadius={78}
                paddingAngle={3}
                dataKey="percentage"
                nameKey="religion"
                labelLine={false}
              >
                {currentList.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColor(entry.religion, index)}
                    stroke="hsl(var(--card))"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const dataPoint = payload[0].payload as ReligionItem;
                    const color = getColor(dataPoint.religion, 0);
                    return (
                      <div
                        style={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 10,
                          padding: "8px 12px",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                          color: "hsl(var(--popover-foreground))",
                          fontSize: 12,
                          minWidth: 130,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontWeight: 700 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: color,
                              display: "inline-block",
                            }}
                          />
                          <span>{dataPoint.religion}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
                          <span>Count:</span>
                          <strong style={{ color: "hsl(var(--foreground))" }}>{dataPoint.count}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
                          <span>Share:</span>
                          <strong style={{ color: color }}>{dataPoint.percentage}%</strong>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend list */}
      {!loading && currentList.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "6px 14px",
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid hsl(var(--border) / 0.4)",
          }}
        >
          {currentList.map((entry, index) => {
            const color = getColor(entry.religion, index);
            return (
              <div
                key={entry.religion}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "hsl(var(--foreground))",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 500 }}>{entry.religion}</span>
                <span
                  style={{
                    fontSize: 11,
                    color: "hsl(var(--muted-foreground))",
                    fontWeight: 600,
                  }}
                >
                  {entry.percentage}% ({entry.count})
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PieChartBox;
